import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users,
  DollarSign,
  Store,
  Search,
  CreditCard,
  CheckCircle,
  Clock,
  TrendingUp,
  Eye,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ title, value, subtitle, icon: Icon, bgColor, iconColor }) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 ${bgColor} rounded-full`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SalespersonRow({ sp, onViewHistory, onPayout }) {
  const hasPending = sp.pending_payout >= 1.0;
  const hasStripe = Boolean(sp.stripe_account_id);
  
  return (
    <tr className="hover:bg-slate-50" data-testid={`salesperson-row-${sp.salesperson_id}`}>
      <td className="px-6 py-4">
        <div>
          <p className="font-medium text-slate-900">{sp.name}</p>
          <p className="text-sm text-slate-500">{sp.email}</p>
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <Badge variant="outline">
          <Store className="w-3 h-3 mr-1" />
          {sp.restaurants_onboarded}
        </Badge>
      </td>
      <td className="px-6 py-4 text-right">
        <span className="font-medium">£{sp.total_earned.toFixed(2)}</span>
      </td>
      <td className="px-6 py-4 text-right">
        <span className={`font-medium ${sp.pending_payout > 0 ? 'text-yellow-600' : 'text-slate-400'}`}>
          £{sp.pending_payout.toFixed(2)}
        </span>
      </td>
      <td className="px-6 py-4 text-right">
        <span className="font-medium text-green-600">£{sp.total_paid.toFixed(2)}</span>
      </td>
      <td className="px-6 py-4 text-center">
        {hasStripe ? (
          <Badge variant="default" className="bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            Connected
          </Badge>
        ) : (
          <Badge variant="secondary">
            <AlertCircle className="w-3 h-3 mr-1" />
            Not Connected
          </Badge>
        )}
      </td>
      <td className="px-6 py-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => onViewHistory(sp)}>
            <Eye className="w-4 h-4 mr-1" />
            History
          </Button>
          {hasStripe && hasPending && (
            <Button size="sm" variant="default" onClick={() => onPayout(sp)}>
              <CreditCard className="w-4 h-4 mr-1" />
              Pay
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function SalespersonPayouts() {
  const [salespeople, setSalespeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSalesperson, setSelectedSalesperson] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [payoutConfirmOpen, setPayoutConfirmOpen] = useState(false);
  const [processingPayout, setProcessingPayout] = useState(false);

  useEffect(() => {
    fetchSalespeople();
  }, []);

  const fetchSalespeople = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/salesperson-payouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSalespeople(response.data);
    } catch (error) {
      console.error('Failed to fetch salesperson data:', error);
      toast.error('Failed to load salesperson payouts');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayoutHistory = async (salespersonId) => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/admin/salesperson-payouts/${salespersonId}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayoutHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch payout history:', error);
      toast.error('Failed to load payout history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewHistory = (sp) => {
    setSelectedSalesperson(sp);
    fetchPayoutHistory(sp.salesperson_id);
  };

  const handlePayoutClick = (sp) => {
    setSelectedSalesperson(sp);
    setPayoutConfirmOpen(true);
  };

  const handleProcessPayout = async () => {
    if (!selectedSalesperson) return;
    
    try {
      setProcessingPayout(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/admin/salesperson-payouts/${selectedSalesperson.salesperson_id}/payout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const amount = response.data.amount;
      toast.success(`Payout of £${amount.toFixed(2)} processed successfully!`);
      setPayoutConfirmOpen(false);
      fetchSalespeople();
      fetchPayoutHistory(selectedSalesperson.salesperson_id);
    } catch (error) {
      const message = error.response ? error.response.data.detail : 'Failed to process payout';
      toast.error(message);
    } finally {
      setProcessingPayout(false);
    }
  };

  const filteredSalespeople = salespeople.filter((sp) => {
    const nameMatch = sp.name ? sp.name.toLowerCase().includes(searchQuery.toLowerCase()) : false;
    const emailMatch = sp.email ? sp.email.toLowerCase().includes(searchQuery.toLowerCase()) : false;
    return nameMatch || emailMatch;
  });

  // Calculate totals
  let totalPending = 0;
  let totalPaid = 0;
  let totalRestaurants = 0;
  
  for (let i = 0; i < salespeople.length; i++) {
    const sp = salespeople[i];
    totalPending += sp.pending_payout || 0;
    totalPaid += sp.total_paid || 0;
    totalRestaurants += sp.restaurants_onboarded || 0;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  const spCount = salespeople.length;

  return (
    <div className="space-y-6" data-testid="salesperson-payouts-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Salesperson Payouts</h1>
          <p className="text-slate-500 mt-1">Manage 0.3% recurring commissions</p>
        </div>
        <Button onClick={fetchSalespeople} variant="outline" data-testid="refresh-btn">
          <TrendingUp className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Salespeople"
          value={spCount}
          subtitle={`${totalRestaurants} restaurants onboarded`}
          icon={Users}
          bgColor="bg-blue-50"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Pending Payouts"
          value={`£${totalPending.toFixed(2)}`}
          subtitle="Awaiting payment"
          icon={Clock}
          bgColor="bg-yellow-50"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="Total Paid"
          value={`£${totalPaid.toFixed(2)}`}
          subtitle="All time"
          icon={CheckCircle}
          bgColor="bg-green-50"
          iconColor="text-green-600"
        />
        <StatCard
          title="Commission Rate"
          value="0.3%"
          subtitle="Recurring on all orders"
          icon={DollarSign}
          bgColor="bg-purple-50"
          iconColor="text-purple-600"
        />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input
          placeholder="Search salespeople..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="salesperson-search-input"
        />
      </div>

      {/* Salespeople Table */}
      <Card data-testid="salespeople-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Salesperson</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-slate-500">Restaurants</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Total Earned</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Pending</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Paid</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-slate-500">Stripe</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredSalespeople.map((sp) => (
                  <SalespersonRow
                    key={sp.salesperson_id}
                    sp={sp}
                    onViewHistory={handleViewHistory}
                    onPayout={handlePayoutClick}
                  />
                ))}
                {filteredSalespeople.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                      No salespeople found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Payout History Modal */}
      <Dialog open={Boolean(selectedSalesperson) && !payoutConfirmOpen} onOpenChange={() => setSelectedSalesperson(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Payout History - {selectedSalesperson ? selectedSalesperson.name : ''}
            </DialogTitle>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
            </div>
          ) : payoutHistory ? (
            <PayoutHistoryContent history={payoutHistory} salesperson={selectedSalesperson} />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Payout Confirmation Dialog */}
      <AlertDialog open={payoutConfirmOpen} onOpenChange={setPayoutConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Payout</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to process a payout of{' '}
              <span className="font-bold text-green-600">
                £{selectedSalesperson ? selectedSalesperson.pending_payout.toFixed(2) : '0.00'}
              </span>{' '}
              to {selectedSalesperson ? selectedSalesperson.name : ''}.
              <br /><br />
              This will transfer funds to their connected Stripe account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingPayout}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleProcessPayout}
              disabled={processingPayout}
              className="bg-green-600 hover:bg-green-700"
            >
              {processingPayout ? 'Processing...' : 'Process Payout'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PayoutHistoryContent({ history, salesperson }) {
  const payouts = history.payouts || [];
  const recentCommissions = history.recent_commissions || [];
  const totalCommissions = history.total_commissions || 0;
  const totalPayouts = history.total_payouts || 0;
  const pendingAmount = salesperson ? salesperson.pending_payout : 0;
  const paidAmount = salesperson ? salesperson.total_paid : 0;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-blue-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-blue-600">{totalCommissions}</p>
          <p className="text-sm text-slate-600">Total Commissions</p>
        </div>
        <div className="p-4 bg-green-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-green-600">{totalPayouts}</p>
          <p className="text-sm text-slate-600">Payouts Made</p>
        </div>
        <div className="p-4 bg-yellow-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-yellow-600">£{pendingAmount.toFixed(2)}</p>
          <p className="text-sm text-slate-600">Pending</p>
        </div>
        <div className="p-4 bg-purple-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-purple-600">£{paidAmount.toFixed(2)}</p>
          <p className="text-sm text-slate-600">Total Paid</p>
        </div>
      </div>

      {/* Payouts List */}
      {payouts.length > 0 && (
        <div>
          <h4 className="font-medium text-slate-900 mb-3">Payout History</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {payouts.map((payout) => (
              <div key={payout.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">£{payout.amount.toFixed(2)}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(payout.created_at).toLocaleDateString()} - {payout.commission_count} commissions
                  </p>
                </div>
                <Badge variant="default" className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Paid
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Commissions */}
      {recentCommissions.length > 0 && (
        <div>
          <h4 className="font-medium text-slate-900 mb-3">Recent Commissions</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {recentCommissions.slice(0, 20).map((c) => {
              const restaurantName = c.restaurant_name || 'Restaurant';
              const orderAmount = c.order_amount || 0;
              const commission = c.sales_commission || 0;
              const createdAt = c.created_at ? new Date(c.created_at).toLocaleDateString() : 'N/A';
              const isPaid = c.payout_status === 'paid';
              
              return (
                <div key={c.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{restaurantName}</p>
                    <p className="text-xs text-slate-500">Order: £{orderAmount.toFixed(2)} - {createdAt}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-green-600">+£{commission.toFixed(2)}</p>
                    <Badge variant={isPaid ? 'default' : 'secondary'} className="text-xs">
                      {isPaid ? 'Paid' : 'Pending'}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
